import { DefaultVisibleAttributes } from '@/components/nodeGraph/utils/constants';
import { prettifyAttributeValue } from '@/components/nodeGraph/utils/graphConstruction';
import { extractRootDomain } from '@/components/nodeGraph/utils/seedFormatting';
import {
  ConsolidatedGraph,
  GraphEntityType,
} from '@/components/nodeGraph/utils/types';
import {
  parseTechnologyProductName,
  parseTechnologyVersion,
} from '@/sections/technology/utils';
import {
  Asset,
  Attribute,
  ResourceType,
  Seed,
  Technology,
  TreeNode,
} from '@/types';

/**
 * Adds Asset nodes to the consolidated graph.
 * Creates nodes with all relationship arrays initialized and adds default attributes.
 *
 * @param graph - The consolidated graph to modify
 * @param assets - Array of assets to add to the graph
 */
export const addAssetsToGraph = (graph: ConsolidatedGraph, assets: Asset[]) => {
  assets.forEach(asset => {
    graph[asset.key] = {
      name: `${asset.dns} ${asset.dns !== asset.name ? asset.name : ''}`,
      key: asset.key,
      type: ResourceType.Asset,
      data: asset,
      DISCOVERED: [],
      HAS_VULNERABILITY: [],
      HAS_ATTRIBUTE: [],
      HAS_TECHNOLOGY: [],
      HAS_WEBPAGE: [],
      HAS_TAG: [],
    };

    // Add direct asset attributes from asset properties
    Object.entries(asset).forEach(([name, value]) => {
      if (
        DefaultVisibleAttributes.includes(name) &&
        value !== undefined &&
        value !== null
      ) {
        const arrayValue = Array.isArray(value) ? value : [value];
        arrayValue.forEach(value => {
          const finalValue = prettifyAttributeValue({
            field: name,
            value,
          });

          const attributeId = `#attribute#${name}#${value}`;
          graph[asset.key].HAS_ATTRIBUTE!.push(attributeId);

          graph[attributeId] = {
            name: `${name}: ${finalValue}`,
            key: attributeId,
            type: ResourceType.Attribute,
            data: { key: attributeId, name, value: finalValue } as Attribute,
          };
        });
      }
    });
  });
};

/**
 * Adds Seed nodes and their relationships to assets in the consolidated graph.
 * Creates seed nodes with enhanced labeling including domain info and status.
 *
 * @param graph - The consolidated graph to modify
 * @param assetToSeedData - Tree node data containing asset-to-seed relationships
 */
export const addSeedsToGraph = (
  graph: ConsolidatedGraph,
  assetToSeedData: TreeNode[]
) => {
  (assetToSeedData || []).forEach(resp => {
    const assetKey = resp.node.result.assets?.[0].key;
    const seed = resp.node.relationships?.[0].target.result.seeds?.[0] as Seed;

    if (!seed || !assetKey) return;

    // Add seed node if not exists
    if (!graph[seed.key]) {
      // Enhanced seed labeling with domain info and status
      const rootDomain = extractRootDomain(seed.identifier);
      const statusInfo = seed.status !== 'A' ? ` [${seed.status}]` : '';

      // Create informative label
      const label = `${seed.identifier}${
        seed.identifier !== rootDomain ? ` (${rootDomain})` : ''
      }${statusInfo}`;

      graph[seed.key] = {
        name: label,
        key: seed.key,
        type: ResourceType.Seed,
        data: seed,
        DISCOVERED: [],
      };
    }

    // Add relationship
    if (graph[assetKey]) {
      graph[assetKey].DISCOVERED!.push(seed.key);
    }
  });
};

/**
 * Adds Risk nodes and their vulnerability relationships to assets in the consolidated graph.
 *
 * @param graph - The consolidated graph to modify
 * @param assetToRiskData - Tree node data containing asset-to-risk relationships
 */
export const addRisksToGraph = (
  graph: ConsolidatedGraph,
  assetToRiskData: TreeNode[]
) => {
  (assetToRiskData || []).forEach(resp => {
    const assetKey =
      resp.node.relationships?.[0].source.result?.assets?.[0].key;
    const risk = resp.node.result?.risks?.[0];

    if (!risk || !assetKey) return;

    // Add risk node if not exists
    if (!graph[risk.key]) {
      graph[risk.key] = {
        name: `${risk.name} ${risk.dns}`,
        key: risk.key,
        type: ResourceType.Risk,
        data: risk,
        HAS_VULNERABILITY: [],
        HAS_ATTRIBUTE: [],
      };
    }

    // Add relationship
    if (graph[assetKey]) {
      graph[assetKey].HAS_VULNERABILITY!.push(risk.key);
    }
  });
};

/**
 * Adds Asset nodes discovered from risks (reverse relationship).
 * Creates asset nodes for assets that were discovered through risk relationships.
 *
 * @param graph - The consolidated graph to modify
 * @param riskToAssetData - Tree node data containing risk-to-asset relationships
 */
export const addRiskAssetsToGraph = (
  graph: ConsolidatedGraph,
  riskToAssetData: TreeNode[]
) => {
  (riskToAssetData || []).forEach(resp => {
    const riskKey = resp.node.relationships?.[0].target.result?.risks?.[0].key;
    const asset = resp.node.result?.assets?.[0];

    if (!asset || !riskKey) return;

    // Add asset node if not exists (discovered assets)
    if (!graph[asset.key]) {
      graph[asset.key] = {
        name: `${asset.dns} ${asset.dns !== asset.name ? asset.name : ''}`,
        key: asset.key,
        type: ResourceType.Asset,
        data: asset,
        DISCOVERED: [],
        HAS_VULNERABILITY: [],
        HAS_ATTRIBUTE: [],
        HAS_TECHNOLOGY: [],
        HAS_WEBPAGE: [],
        HAS_TAG: [],
      };
    }

    // Add reverse relationship (risk has this asset)
    if (graph[riskKey]) {
      if (!graph[riskKey].HAS_VULNERABILITY) {
        graph[riskKey].HAS_VULNERABILITY = [];
      }
      graph[riskKey].HAS_VULNERABILITY!.push(asset.key);
    }
  });
};

/**
 * Adds Technology nodes and their relationships to assets in the consolidated graph.
 *
 * @param graph - The consolidated graph to modify
 * @param technologiesData - Tree node data containing asset-to-technology relationships
 */
export const addTechnologiesToGraph = (
  graph: ConsolidatedGraph,
  technologiesData: TreeNode[]
) => {
  (technologiesData || []).forEach(resp => {
    const technology = resp.node.relationships?.[0].target.relationships?.[0]
      .target.result.technologies?.[0] as Technology;
    const asset = resp.node.result?.assets?.[0] as Asset;

    if (!technology || !asset) return;

    const label =
      technology.name || parseTechnologyProductName(technology.product) || '-';
    const description = parseTechnologyVersion(technology.version, true);

    // Add technology node if not exists
    if (!graph[technology.key]) {
      graph[technology.key] = {
        name: `${label} ${description}`,
        key: technology.key,
        type: ResourceType.Technology,
        data: technology,
        HAS_ATTRIBUTE: [],
      };
    }

    // Add relationship
    if (graph[asset.key]) {
      if (!graph[asset.key].HAS_TECHNOLOGY) {
        graph[asset.key].HAS_TECHNOLOGY = [];
      }
      graph[asset.key].HAS_TECHNOLOGY!.push(technology.key);
    }
  });
};

/**
 * Adds Webpage nodes and their relationships to assets in the consolidated graph.
 *
 * @param graph - The consolidated graph to modify
 * @param webpagesData - Tree node data containing webpage data
 */
export const addWebpagesToGraph = (
  graph: ConsolidatedGraph,
  webpagesData: TreeNode[]
) => {
  (webpagesData || []).forEach(resp => {
    const webpage = resp.node.result?.webpages?.[0];
    const attribute =
      resp.node.relationships?.[0].source?.result?.attributes?.[0];

    if (!webpage || !attribute) return;

    // Add webpage node if not exists
    if (!graph[webpage.key]) {
      graph[webpage.key] = {
        name: webpage.url,
        key: webpage.key,
        type: ResourceType.Webpage,
        data: webpage,
      };
    }

    // Add relationship (attribute.source should be the parent asset)
    if (graph[attribute.source]) {
      if (!graph[attribute.source].HAS_WEBPAGE) {
        graph[attribute.source].HAS_WEBPAGE = [];
      }
      graph[attribute.source].HAS_WEBPAGE!.push(webpage.key);
    }
  });
};

/**
 * Adds AD (Active Directory) object nodes to the graph for SharpHound clustering.
 * Creates nodes for AD users, computers, groups, domains, OUs, and GPOs.
 *
 * @param graph - The consolidated graph to modify
 * @param adObjectsData - Tree node data containing AD object data
 */
export const addADObjectsToGraph = (
  graph: ConsolidatedGraph,
  adObjectsData: TreeNode[]
) => {
  // Helper to add a single AD Asset to graph
  const addSingleADObject = (asset: Asset) => {
    if (!asset || graph[asset.key]) return; // Skip if already added

    graph[asset.key] = {
      name: asset.name || asset.dns || asset.key,
      key: asset.key,
      type: 'SharpHound' as GraphEntityType,
      data: asset,
      AD_RELATIONSHIP: [], // Store AD relationships
    };

    // Add attributes for AD object properties
    const adClass = (asset as any).class;
    if (adClass) {
      const finalValue = prettifyAttributeValue({
        field: 'class',
        value: adClass,
      });

      const attributeId = `#ad-attribute#class#${adClass}`;
      if (!graph[asset.key].HAS_ATTRIBUTE) {
        graph[asset.key].HAS_ATTRIBUTE = [];
      }
      graph[asset.key].HAS_ATTRIBUTE!.push(attributeId);

      graph[attributeId] = {
        name: `AD Type: ${finalValue}`,
        key: attributeId,
        type: ResourceType.Attribute,
        data: {
          key: attributeId,
          name: 'class',
          value: finalValue,
          source: asset.key,
          username: asset.username || '',
          status: 'A',
          created: asset.created || new Date().toISOString(),
          capability: 'SharpHound',
          visited: asset.visited || new Date().toISOString(),
          ttl: 0,
          metadata: {},
        } as Attribute,
      };
    }
  };

  (adObjectsData || []).forEach(resp => {
    // Add the main Asset (AD object)
    const asset = resp.node.result?.assets?.[0];
    if (asset) {
      addSingleADObject(asset);
    }

    // Add target Assets from relationships
    if (resp.node.relationships) {
      resp.node.relationships.forEach(relationship => {
        const targetAsset = relationship.target?.result?.assets?.[0];
        if (targetAsset) {
          addSingleADObject(targetAsset);
        }
      });
    }
  });
};

/**
 * Adds AD relationship edges between AD objects in the graph.
 * Processes relationships like MemberOf, AdminTo, CanRDP, etc.
 *
 * @param graph - The consolidated graph to modify
 * @param adObjectsData - Tree node data containing AD relationship data
 */
export const addADRelationshipsToGraph = (
  graph: ConsolidatedGraph,
  adObjectsData: TreeNode[]
) => {
  (adObjectsData || []).forEach(resp => {
    const sourceAsset = resp.node.result?.assets?.[0];
    if (!sourceAsset || !resp.node.relationships) return;

    const sourceKey = sourceAsset.key;

    // Process each relationship from this Asset
    resp.node.relationships.forEach(relationship => {
      if (!relationship.target || !relationship.target.result) return;

      const targetAsset = relationship.target.result.assets?.[0];
      if (!targetAsset) return;

      const targetKey = targetAsset.key;
      const relationshipType = relationship.label;

      // Ensure both source and target exist in graph
      if (graph[sourceKey] && graph[targetKey]) {
        if (!graph[sourceKey].AD_RELATIONSHIP) {
          graph[sourceKey].AD_RELATIONSHIP = [];
        }
        // Store relationship info including type
        graph[sourceKey].AD_RELATIONSHIP!.push(
          `${targetKey}:${relationshipType}`
        );
      }
    });
  });
};
